import { useMutation, useQueryClient } from "@tanstack/react-query"
import { FileText, Trash2 } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { type Adjuntando, AdjuntosNuevos, sumar } from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"

export type SoporteCxp = {
  id: number
  correlativo: number
  nombre: string
  nombre_original: string
  mime: string
  bytes: number
  creado_en: string
}

type Props = {
  cxpId: number
  concepto: string
  soportes: SoporteCxp[]
  /** Sin esto el modal es de sólo lectura: ver un soporte no es poder subirlo. */
  puedeEscribir: boolean
  onCerrar: () => void
}

function pesar(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Los soportes de una cuenta por pagar: lo que MOTIVA la deuda.
 *
 * ⚠ **No es el comprobante del pago.** El papel que prueba que el dinero salió vive en
 * el movimiento del banco —o en la ejecución de la orden, si la hubo—; aquí va la
 * factura del proveedor, su nota de entrega, el acuerdo: lo que explica por qué se le
 * debe. Confundirlos deja la deuda sin justificar y el pago sin probar.
 *
 * ⚠ **Se adjunta con `AdjuntosNuevos`, como todo lo demás** —elegir, arrastrar y
 * Ctrl+V—: dos zonas de adjuntar con comportamientos distintos serían dos sitios donde
 * aprender lo mismo.
 *
 * ⚠ **El archivo se sube de uno en uno y el modal no se cierra al terminar.** Una
 * factura suele venir con su nota de entrega detrás, y cerrar obligaría a volver a
 * abrir; además, si el servidor rechaza uno, se ve cuál fue.
 */
export function ModalSoportesCxp({ cxpId, concepto, soportes, puedeEscribir, onCerrar }: Props) {
  const clienteQuery = useQueryClient()
  const [adjuntos, setAdjuntos] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])
  const [viendo, setViendo] = useState<SoporteCxp | null>(null)

  const subir = useMutation({
    mutationFn: async (pendientes: Adjuntando[]) => {
      // De uno en uno y en orden: el correlativo lo asigna el servidor con el mayor
      // más uno, y en paralelo dos subidas pelearían por el mismo número.
      for (const adjunto of pendientes) {
        const cuerpo = new FormData()
        cuerpo.append("archivo", adjunto.archivo)
        await api.post(`/api/v1/cxp/${cxpId}/documentos`, cuerpo)
      }
    },
    onSuccess: () => {
      setAdjuntos([])
      setRechazados([])
      clienteQuery.invalidateQueries({ queryKey: ["cxp"] })
    },
  })

  const borrar = useMutation({
    mutationFn: (documentoId: number) =>
      api.delete(`/api/v1/cxp/${cxpId}/documentos/${documentoId}`),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["cxp"] }),
  })

  return (
    <>
      <Modal
        abierto={true}
        titulo="Soportes de la cuenta por pagar"
        descripcion={concepto}
        onCerrar={onCerrar}
      >
        <div className="space-y-4">
          {soportes.length === 0 ? (
            <p
              className="superficie border-dashed p-4 text-center text-sm"
              style={{ color: "var(--fg-muted)" }}
            >
              Esta cuenta no tiene todavía el documento que la motiva.
            </p>
          ) : (
            <ul className="space-y-2">
              {soportes.map((s) => (
                <li key={s.id} className="superficie flex items-center gap-3 p-3">
                  <FileText size={16} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
                  <button
                    type="button"
                    onClick={() => setViendo(s)}
                    className="foco-cristal min-w-0 flex-1 text-left"
                  >
                    <span className="block truncate text-sm font-medium">{s.nombre}</span>
                    <span className="block truncate text-xs" style={{ color: "var(--fg-muted)" }}>
                      {s.nombre_original} · {pesar(s.bytes)}
                    </span>
                  </button>
                  {puedeEscribir && (
                    <button
                      type="button"
                      onClick={() => borrar.mutate(s.id)}
                      disabled={borrar.isPending}
                      aria-label={`Quitar ${s.nombre}`}
                      className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                      style={{ color: "var(--destructive)" }}
                    >
                      <Trash2 size={15} aria-hidden={true} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {puedeEscribir && (
            <div className="space-y-3">
              <AdjuntosNuevos
                adjuntos={adjuntos}
                rechazados={rechazados}
                etiqueta="Adjuntar soporte"
                onArchivos={(archivos) => {
                  const resultado = sumar(adjuntos, archivos)
                  setAdjuntos(resultado.adjuntos)
                  setRechazados(resultado.rechazados)
                }}
                onQuitar={(id) => setAdjuntos((previos) => previos.filter((a) => a.id !== id))}
              />
              {adjuntos.length > 0 && (
                <Boton onClick={() => subir.mutate(adjuntos)} disabled={subir.isPending}>
                  {subir.isPending ? "Subiendo…" : `Subir ${adjuntos.length}`}
                </Boton>
              )}
              {subir.isError && (
                <p className="text-sm" style={{ color: "var(--destructive)" }}>
                  No se pudo subir el soporte.
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>

      <VistaPreviaArchivo
        archivo={
          viendo && {
            ruta: `/api/v1/cxp/${cxpId}/documentos/${viendo.id}/contenido`,
            nombre: viendo.nombre,
          }
        }
        onCerrar={() => setViendo(null)}
      />
    </>
  )
}
